import { useI18n } from '@/i18n/I18nProvider';
/** Valor escalar legível; objetos e arrays não passam por aqui (ver `ehEstruturado`). */
export function formatarValor(valor, opcoes) {
    if (valor === null || valor === undefined || valor === '') {
        return '—';
    }
    if (typeof valor === 'number') {
        return opcoes?.numero !== undefined
            ? opcoes.numero(valor)
            : valor.toLocaleString('pt-BR', { maximumFractionDigits: 6 });
    }
    if (typeof valor === 'boolean') {
        return valor ? (opcoes?.verdadeiro ?? 'sim') : (opcoes?.falso ?? 'não');
    }
    if (typeof valor === 'string') {
        return valor;
    }
    return JSON.stringify(valor);
}
/** Objetos e arrays dos atributos são apresentados como JSON legível. */
export function ehEstruturado(valor) {
    return typeof valor === 'object' && valor !== null;
}
export function jsonLegivel(valor) {
    return JSON.stringify(valor, null, 2);
}
/** Primeiros pares chave → valor de uma feição, sem atribuir significado a nenhum campo. */
export function resumirAtributos(atributos, maximo = 2, opcoes) {
    return Object.entries(atributos)
        .filter(([, valor]) => !ehEstruturado(valor))
        .slice(0, maximo)
        .map(([chave, valor]) => `${chave}: ${formatarValor(valor, opcoes)}`)
        .join(' · ');
}
const CAMPOS_TECNICOS = new Set([
    'id',
    'fid',
    'objectid',
    'gid',
    'uuid',
]);
const PREFIXOS_TECNICOS = ['cod_', 'codigo', 'hash'];
const EXTENSOES_TECNICAS = [
    '.shp',
    '.gpkg',
    '.geojson',
    '.json',
    '.csv',
    '.tif',
    '.tiff',
    '.zip',
    '.dbf',
    '.prj',
    '.shx',
    '.kml',
    '.kmz',
    '.xml',
];
function ehNomeTecnico(chave) {
    const normalizada = chave.toLowerCase();
    if (CAMPOS_TECNICOS.has(normalizada)) {
        return true;
    }
    return PREFIXOS_TECNICOS.some((prefixo) => normalizada.startsWith(prefixo));
}
function ehValorPuramenteNumerico(valor) {
    return valor.trim() !== '' && !Number.isNaN(Number(valor));
}
function ehCaminhoOuArquivoTecnico(valor) {
    // Caminhos Windows: C:\, D:\, E:\..., caminhos UNC \\servidor\..., relativos Windows .\
    if (/^[a-zA-Z]:[\\/]/.test(valor)) {
        return true;
    }
    // Caminhos UNC
    if (valueStartsWithUnc(valor)) {
        return true;
    }
    // Caminhos Unix absolutos (/, /home, /tmp, /data) ou relativos Unix (./  ../)
    if (/^(\.{0,2}\/)/.test(valor)) {
        return true;
    }
    // URLs http/https/file
    if (/^(https?|file):\/\//i.test(valor)) {
        return true;
    }
    // Arquivos cuja extensão é claramente técnica (.shp, .gpkg, ...)
    const normalizada = valor.toLowerCase();
    return EXTENSOES_TECNICAS.some((extensao) => normalizada.endsWith(extensao));
}
function valueStartsWithUnc(valor) {
    return valor.startsWith('\\\\') || valor.startsWith('//');
}
function valorTextualUtil(valor) {
    if (typeof valor !== 'string') {
        return null;
    }
    const texto = valor.trim();
    if (texto === '') {
        return null;
    }
    if (ehCaminhoOuArquivoTecnico(texto)) {
        return null;
    }
    return texto;
}
// Classificador central de campos nominais humanos. Case-insensitive.
// Usado tanto por `rotuloHumano` (sobre os atributos de uma feição) quanto por
// `melhorAtributoDeRotulo` (sobre os nomes dos atributos publicados na camada),
// para que ambos os classificadores comam os mesmos critérios.
//
// Ordem:
//   1. Chave exata:    nome, name, label, titulo, denominacao, descricao
//   2. Prefixo nominal: nome_*, name_*, nm_*
//   3. Sufixo nominal:  *_nome, *_name, *_nm
//
// Nunca retorna `id`, `fid`, `objectid`, `gid`, `uuid`, `cod_*`, `codigo*`, `hash*`,
// nem valores puramente numéricos, nem caminhos/URLs/arquivos técnicos.
const CHAVES_EXATAS = ['nome', 'name', 'label', 'titulo', 'denominacao', 'descricao'];
const PREFIXOS_NOMINAIS = ['nome_', 'name_', 'nm_'];
const SUFIXOS_NOMINAIS = ['_nome', '_name', '_nm'];
function classificarComoNominal(chave) {
    const normalizada = chave.toLowerCase();
    if (ehNomeTecnico(normalizada)) {
        return null;
    }
    if (CHAVES_EXATAS.some((exata) => normalizada === exata)) {
        return 'exata';
    }
    if (PREFIXOS_NOMINAIS.some((prefixo) => normalizada.startsWith(prefixo))) {
        return 'prefixo';
    }
    if (SUFIXOS_NOMINAIS.some((sufixo) => normalizada.endsWith(sufixo))) {
        return 'sufixo';
    }
    return null;
}
function valorNominal(valor) {
    const texto = valorTextualUtil(valor);
    if (texto === null) {
        return null;
    }
    if (ehValorPuramenteNumerico(texto)) {
        return null;
    }
    return texto;
}
/**
 * Escolhe o rótulo humano de uma feição a partir dos atributos já recebidos, sem
 * conhecimento de domínio: apenas o formato do nome do campo decide a precedência.
 *
 * A ordem das pre-IDs é compartilhada com `melhorAtributoDeRotulo` via
 * `classificarComoNominal` — qualquer novo padrão aplicado aqui deve ser refletido lá.
 */
export function rotuloHumano(atributos, id, rotuloPadrao) {
    const pares = Object.entries(atributos ?? {});
    // 1. Chaves exatas.
    for (const [chave, valor] of pares) {
        if (classificarComoNominal(chave) === 'exata') {
            const texto = valorNominal(valor);
            if (texto !== null) {
                return texto;
            }
        }
    }
    // 2. Prefixos nominais (`nome_*`, `name_*`, `nm_*`). Em ordem de precedência entre si.
    for (const prefixo of PREFIXOS_NOMINAIS) {
        for (const [chave, valor] of pares) {
            if (chave.toLowerCase().startsWith(prefixo) && !ehNomeTecnico(chave.toLowerCase())) {
                const texto = valorNominal(valor);
                if (texto !== null) {
                    return texto;
                }
            }
        }
    }
    // 3. Sufixos nominais (`*_nome`, `*_name`, `*_nm`). Em ordem de precedência entre si.
    for (const sufixo of SUFIXOS_NOMINAIS) {
        for (const [chave, valor] of pares) {
            if (chave.toLowerCase().endsWith(sufixo) && !ehNomeTecnico(chave.toLowerCase())) {
                const texto = valorNominal(valor);
                if (texto !== null) {
                    return texto;
                }
            }
        }
    }
    // Sem fallback para a primeira string disponível: SIGLA, códigos, IDs, caminhos e
    // valores puramente numéricos já foram descartados. Se nada servir, cai no id técnico.
    return rotuloPadrao !== undefined ? rotuloPadrao(id) : `Feição ${id}`;
}
/**
 * Versões de `rotuloHumano` e `formatarValor` no idioma ativo. As funções devolvidas mudam de
 * identidade com o idioma: usar apenas na renderização, nunca em dependências de efeitos.
 */
export function useApresentacao() {
    const { t, formatar } = useI18n();
    const opcoes = {
        numero: (numero) => formatar.numero(numero, { maximumFractionDigits: 6 }),
        verdadeiro: t('feicao.valorSim'),
        falso: t('feicao.valorNao'),
    };
    return {
        rotulo: (atributos, id) => rotuloHumano(atributos, id, (identificador) => t('feicao.rotuloPadrao', { id: String(identificador) })),
        valor: (valor) => formatarValor(valor, opcoes),
    };
}
/**
 * Escolhe, entre os nomes de atributo publicados por uma camada, o melhor candidato
 * para busca textual simples — mesma heurística de nome de campo usada em `rotuloHumano`.
 */
export function melhorAtributoDeRotulo(atributosDisponiveis) {
    if (atributosDisponiveis.length === 0) {
        return null;
    }
    // 1. Chave exata.
    for (const alvo of CHAVES_EXATAS) {
        const encontrado = atributosDisponiveis.find((nome) => nome.toLowerCase() === alvo);
        if (encontrado !== undefined) {
            return encontrado;
        }
    }
    // 2. Prefixos nominais.
    for (const prefixo of PREFIXOS_NOMINAIS) {
        const encontrado = atributosDisponiveis.find((nome) => nome.toLowerCase().startsWith(prefixo));
        if (encontrado !== undefined) {
            return encontrado;
        }
    }
    // 3. Sufixos nominais.
    for (const sufixo of SUFIXOS_NOMINAIS) {
        const encontrado = atributosDisponiveis.find((nome) => nome.toLowerCase().endsWith(sufixo));
        if (encontrado !== undefined) {
            return encontrado;
        }
    }
    return null;
}
